import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  BrokerCreateUpdateActions,
  BrokerDetailsActions,
  BrokerListActions,
  MyBrokersActions,
} from '@lockton-apps/brokers/action';
import { ClientBrokersActions } from '@lockton-apps/clients/actions';
import { PolicyCreateUpdateActions } from '@lockton-apps/policies/action';
import { UsersApiActions } from '@lockton-apps/shared/action/users';
import { User } from '@lockton-apps/shared/interfaces';
import {
  UserCreateUpdateActions,
  UserDetailsActions,
  UserListActions,
} from '@lockton-apps/users/actions';

export const USERS_FEATURE_KEY = 'users';

export interface UsersState extends EntityState<User> {
  selectedId?: string | number | null; // which Users record has been selected
  selectionIds?: string[];
  loaded: boolean; // has the Users list been loaded
  error?: unknown; // last known error (if any)
  mutationsLoading: boolean;
}

export interface UsersPartialState {
  readonly [USERS_FEATURE_KEY]: UsersState;
}

export const usersAdapter: EntityAdapter<User> = createEntityAdapter<User>({
  selectId: (user: User) => user.userId,
});

export const initialUsersState: UsersState = usersAdapter.getInitialState({
  loaded: false,
  mutationsLoading: false,
  filter: {
    userType: null,
    showDisabled: false,
  },
});

const reducer = createReducer(
  initialUsersState,

  on(
    UserListActions.init,
    UserDetailsActions.init,
    ClientBrokersActions.init,
    BrokerListActions.init,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(UsersApiActions.loadUsersSuccess, (state, { users }) =>
    usersAdapter.setAll(users, { ...state, loaded: true })
  ),
  on(
    UsersApiActions.loadUsersFailure,
    UsersApiActions.loadClientBrokersFailure,
    (state, { error }) => ({
      ...state,
      error,
    })
  ),

  // Select
  on(MyBrokersActions.brokerSelected, (state, { id }) => ({
    ...state,
    selectedId: id,
  })),

  // Select and Load User
  on(
    BrokerDetailsActions.init,
    UserCreateUpdateActions.initUpdate,
    BrokerCreateUpdateActions.initUpdate,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(UsersApiActions.loadUserSuccess, (state, { user }) =>
    usersAdapter.setOne(user, {
      ...state,
      loaded: true,
    })
  ),
  on(
    UsersApiActions.loadUserFailure,
    UsersApiActions.loadAuthenticatedUserFailure,
    (state, { error }) => ({
      ...state,
      error,
    })
  ),
  on(UsersApiActions.loadAuthenticatedUserSuccess, (state, { user }) =>
    usersAdapter.setOne(user, {
      ...state,
      loaded: true,
    })
  ),

  // Add User
  on(
    UserCreateUpdateActions.add,
    UserCreateUpdateActions.update,
    BrokerCreateUpdateActions.added,
    BrokerCreateUpdateActions.updated,
    (state) => ({
      ...state,
      mutationsLoading: true,
    })
  ),
  on(
    UsersApiActions.addSuccess,
    UsersApiActions.addBrokerSuccess,
    (state, { user }) =>
      // Bug in API returnng wrong t2codes
      // usersAdapter.addOne(user, { ...state, mutationsLoading: false })
      ({
        ...state,
        mutationsLoading: false,
      })
  ),
  on(
    UsersApiActions.addFailure,
    UsersApiActions.updateFailure,
    UsersApiActions.addBrokerFailure,
    UsersApiActions.updateBrokerFailure,
    UsersApiActions.userEnabledFailure,
    UsersApiActions.userDeletedFailure,
    (state, { error }) => ({
      ...state,
      mutationsLoading: false,
      error,
    })
  ),

  on(
    UsersApiActions.updateSuccess,
    UsersApiActions.updateBrokerSuccess,
    (state, { user }) =>
      usersAdapter.upsertOne(user, { ...state, mutationsLoading: false })
  ),

  // Enable user
  on(UserDetailsActions.userEnabled, (state) => ({
    ...state,
    mutationsLoading: true,
    error: null,
  })),
  on(UsersApiActions.userEnabledSuccess, (state, { user }) =>
    usersAdapter.upsertOne(
      { ...user, enabled: true },
      {
        ...state,
        mutationsLoading: false,
      }
    )
  ),
  // Delete User
  on(
    UserDetailsActions.userDeleted,
    BrokerDetailsActions.brokerDeleted,
    (state, { user }) => ({
      ...state,
      mutationsLoading: true,
      error: null,
    })
  ),
  on(UsersApiActions.userDeletedSuccess, (state, { user }) =>
    usersAdapter.upsertOne(
      { ...user, enabled: false },
      {
        ...state,
        mutationsLoading: false,
      }
    )
  ),

  on(UsersApiActions.loadClientBrokersSuccess, (state, { users }) =>
    usersAdapter.upsertMany(users, {
      ...state,
      loaded: true,
    })
  ),
  on(UsersApiActions.loadBrokersByT2CodeFailure, (state, { error }) => ({
    ...state,
    loaded: true,
    error,
  })),

  // Reset Selected
  on(
    MyBrokersActions.destroyed,
    MyBrokersActions.brokerDeselected,
    (state) => ({
      ...state,
      selectedId: null,
    })
  ),
  // Reset Selection
  on(
    PolicyCreateUpdateActions.destroyed,
    ClientBrokersActions.destroyed,
    (state) => ({
      ...state,
      selectionIds: [],
    })
  ),
  on(MyBrokersActions.destroyed, (state) => ({
    ...state,
    selectionIds: [],
    selectedId: null,
  }))
);

export function usersReducer(state: UsersState | undefined, action: Action) {
  return reducer(state, action);
}
